import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { registerAuthExpiryHandler, resetAuthExpiryGuard } from './features/auth/expiry';
import { forgetDevice, resetClientCache } from './features/auth/session';
import { ErrorToaster } from './components/ErrorToaster';
import { TouchTitles } from './components/TouchTitles';
import { queryClient } from './lib/trpc';
import { routeTree } from './routeTree.gen';
import './styles.css';

/**
 * The query client is created in lib/trpc.ts, not here.
 *
 * Route guards run outside React and need the same cache the components read — a second
 * client would let the guard believe you are signed in while the page believes you are not.
 */
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent', // prefetch on hover — the cheapest perceived-speed win available
  defaultPreloadStaleTime: 0,
});

/*
 * Start downloading the first page's code now, instead of after the session check.
 *
 * The root guard awaits `auth.me` before the router loads anything, and only then asks for the
 * page's chunks — a round trip that never depended on the answer, because nearly everybody who
 * opens a deep link is signed in. So the matched routes' chunks (component and loader, which are
 * one chunk — see `vite.config.ts`) are requested here, alongside it. If the answer turns out to be
 * "sign in", the redirect happens as before and the download was merely early. Failures are
 * dropped: the router's own load asks again and handles them where it always has.
 */
const [initialRoutes] = router.getMatchedRoutes(router.state.location.pathname);
for (const route of initialRoutes) {
  router.loadRouteChunk(route)?.catch(() => undefined);
}

// The query client is built before the router exists, so it can only report an expired
// session, not act on one — this is where that report becomes a redirect. Cleared like a
// logout, because the identity a page's cached queries belong to is gone either way.
registerAuthExpiryHandler((returnTo) => {
  resetClientCache(queryClient);
  // The server refused the session, so what was kept for offline use goes with it.
  forgetDevice();
  void router
    .navigate({ to: '/login', search: { redirect: returnTo }, replace: true })
    .finally(() => {
      resetAuthExpiryGuard();
    });
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/*
       * BESIDE the router, not inside the shell: the root route's error component replaces the
       * shell when a page cannot render, and the errors have to outlive that. See ErrorToaster.
       * Client-side navigation for Verify, never a reload — the transaction entry queue lives in
       * the browser until it is posted, and a reload would discard it.
       */}
      <ErrorToaster onVerify={() => void router.navigate({ to: '/mfa' })} />
      {/* Beside the router for the same reason: it reads the page, it is not part of one. */}
      <TouchTitles />
    </QueryClientProvider>
  </StrictMode>,
);

/*
 * The service worker — what lets the installed app open with no signal. See `serviceWorker()` in
 * vite.plugins.ts for what it keeps and why.
 *
 * Production builds only: the dev server's modules are not fingerprinted, and a worker caching
 * them would serve yesterday's code to a developer wondering why their change has not appeared.
 * After `load`, so registering it — and the first install downloading the build — never competes
 * with the page's own first paint.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
